import React, { useState } from "react";
import { Box, Typography, TextField, Link, Autocomplete, } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import ReCAPTCHA from "react-google-recaptcha";
import { useSnackbar } from 'notistack';
import { googleReCaptcaSiteKey } from "../services/googleReCaptcaSiteKey";
import CustomFormAction from "../customComponents/CustomFormAction";
import CustomButton from "../customComponents/CustomButton";
import { ContactInputDto, PhoneCountryCode } from "../interfaces/IContact";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ContactService from "../services/ContactService";
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import DownloadIcon from '@mui/icons-material/Download';

const googleReCaptcaKey = googleReCaptcaSiteKey();

const contactSchema = z.object({
    name: z.string().min(2, "First name must be at least 2 characters").max(50),
    message: z.string().min(10, "Message must be at least 10 characters").max(50),
    email: z.string().email("Invalid email format"),
    phoneCountryCode: z.nativeEnum(PhoneCountryCode),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number format"),
    recaptchaToken: z.string()
});

const ContactUs: React.FC = () => {

    const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<ContactInputDto>({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            recaptchaToken: "",
            phoneCountryCode: 1,
        }
    });

    const [loading, setLoading] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    const recaptchaRef = React.useRef<ReCAPTCHA | null>(null);

    const handleRegister = async (data: ContactInputDto, token: string) => {
        setLoading(true);

        try {
            const response = await ContactService.Create({ ...data, recaptchaToken: token ?? '' });
            if (response.response.success) {
                enqueueSnackbar('Message sent', { variant: 'success' });
                reset()
            } else {
                enqueueSnackbar(response.response.error?.message, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Sending failed. Please try again.', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRecaptcha = async (data: ContactInputDto) => {
        recaptchaRef.current?.executeAsync().then((token) => {
            handleRegister(data, token ?? "");
        });
    };

    return (
        <Box
            component="section"
            id="homeContactUs"
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
            }}
        >
            <Box
                sx={{
                    zIndex: '1',
                    backgroundColor: '#dde3e1',
                    // backgroundColor:'#c4d0ce',
                    p: 4,
                }}>
                <Typography variant="h4" align="center" sx={{ fontWeight: "bold", mb: 4, }} >
                    Contact Me
                </Typography>
                <Box
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent={{ xs: "center", md: "space-evenly" }}
                    alignItems='center'
                    gap={4}
                >
                    <Box display="flex" flexDirection="column" gap={2} justifyContent='center' alignItems='center' sx={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
                        <Typography variant="h6" fontWeight="bold">
                            Majid Ghajari
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                            +1 (250) 884-8990
                        </Typography>
                        <Box gap={2} display="flex" alignItems="center">
                            <Link href="mailto:majidgdotcom@gmail.com" target="_blank" sx={{ color: 'text.primary' }} aria-label="Email">
                                <EmailIcon />
                            </Link>
                            <Link href="https://www.linkedin.com/in/majidgdotcom/" target="_blank" sx={{ color: 'text.primary' }} aria-label="LinkedIn">
                                <LinkedInIcon />
                            </Link>
                            <Link href="https://github.com/majidgdotcom" target="_blank" sx={{ color: 'text.primary' }} aria-label="GitHub">
                                <GitHubIcon />
                            </Link>
                        </Box>
                        <Link href="/Majid Ghajari_CV.pdf" target="_blank" rel="noopener noreferrer" sx={{ color: 'text.primary' }} aria-label="Resume"
                            display='flex' gap={0.5}
                        >
                            <Box>My resume</Box><DownloadIcon />
                        </Link>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 300,
                            maxWidth: 400,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <form onSubmit={handleSubmit(handleRecaptcha)} noValidate>
                            <Box mt={2}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    label="Name"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('name')}
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            </Box>

                            <Box mt={2}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    label="Email"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('email')}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            </Box>

                            <Box mt={2}>
                                <Autocomplete
                                    fullWidth
                                    options={['', ...Object.values(PhoneCountryCode).filter(v => typeof v === 'number')] as (PhoneCountryCode | '')[]}
                                    getOptionLabel={(option) => {
                                        if (option === '') return '';
                                        const countryName = PhoneCountryCode[option as number];
                                        return `+${option} (${countryName})`;
                                    }}
                                    value={watch('phoneCountryCode')}
                                    onChange={(_, newValue) =>
                                        setValue('phoneCountryCode', newValue as PhoneCountryCode)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Phone Country Code"
                                            InputLabelProps={{ shrink: true }}
                                            error={!!errors.phoneCountryCode}
                                            helperText={errors.phoneCountryCode?.message ? 'Country code is required' : ''}
                                        />
                                    )}
                                />
                            </Box>

                            <Box mt={2}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    label="Phone"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('phone')}
                                    error={!!errors.phone}
                                    helperText={errors.phone?.message}
                                />
                            </Box>

                            <Box mt={2}>
                                <TextField fullWidth label="Message" InputLabelProps={{ shrink: true }} multiline rows={3} {...register('message')} error={!!errors.message} helperText={errors.message?.message} />
                            </Box>
                            <CustomFormAction>
                                <CustomButton
                                    lable={loading ? 'Sending...' : 'Send'}
                                    type='submit'
                                    disabled={loading}
                                    infoContent={`Submit`}
                                    size='medium'

                                />
                            </CustomFormAction>
                        </form>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            size="invisible"
                            sitekey={googleReCaptcaKey}
                            theme="dark"
                            type="image"
                            className="recaptcha"
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ContactUs;